import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Formats any error type into a human-readable string.
 * Handles Error instances, Supabase error objects, and unknown types.
 */
export function formatError(error: unknown): string {
  if (error instanceof Error) {
    return error.message
  }
  if (typeof error === 'object' && error !== null) {
    // Handle Supabase PostgrestError shape: { message, details, hint, code }
    const obj = error as Record<string, unknown>
    if (typeof obj.message === 'string' && obj.message) {
      return obj.message
    }
    // Handle generic object with details
    if (typeof obj.details === 'string' && obj.details) {
      return obj.details
    }
    // Fallback to JSON for debugging
    try {
      return JSON.stringify(error)
    } catch {
      return '[object Object]'
    }
  }
  return String(error)
}

/**
 * Checks if an error is related to dynamic module import failure.
 * These typically occur after a new deploy when the browser has cached
 * old JS chunks with different hashes.
 */
export function isModuleImportError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error)
  return (
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('Loading chunk') ||
    message.includes('ChunkLoadError')
  )
}
